import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { GROUP_TAB_IDS, TabId } from 'core/utils/groupAlerts';
import { UserRole } from 'types';

import {
  G6_MATERIALS_CRUD,
  DEBTOR_REMINDERS,
  DIPLOMA_SEND_EMAIL,
  DIPLOMA_GENERATION,
  INVOICE_ISSUE,
} from 'core/featureFlags';

import {
  CAREER_ROLES,
  EDUCATION_ROLES,
  EDUCATION_SECTION_ROLES,
  FINANCE_PLAN_ROLES,
} from '../Palette/paletteRoles';
import {
  AGREEMENT_CORRECT_ROLES,
  ENROLLMENT_UNDO_ROLES,
} from '../Students/agreementCorrectionModel';

/**
 * Capability booleans for the Group Dashboard, derived from the current user's
 * roles. Hiding a control is a UX affordance only — the backend remains the
 * authority on each mutation/export.
 */
export interface IGroupPermissions {
  /** Admin, FinanceManager, AccountManager (SalesManager: read). */
  canViewFinance: boolean;
  /** Same set as `canViewFinance`. */
  canExportFinance: boolean;
  /** Admin, AccountManager, SalesManager, FinanceManager. */
  canTerminate: boolean;
  /** (Admin, FinanceManager, AccountManager) AND the `DEBTOR_REMINDERS` flag. */
  canRemindDebtors: boolean;
  /**
   * BULK «Выставить фактуры» over the group — issue REAL Fakturownia VAT
   * documents for many students at once. (Admin, FinanceManager,
   * AccountManager) AND the `INVOICE_ISSUE` flag. Flag-gated for the same
   * reason as the single-student action: every created document gets a fiscal
   * number and CANNOT be deleted from the ERP, so it stays off on a
   * half-configured install. The backend is the authority
   * (`FAKTUROWNIA_ACCESS_ROLES`).
   */
  canIssueFaktura: boolean;
  /** (Admin, Methodist, Teacher) AND the `G6_MATERIALS_CRUD` flag. */
  canManageMaterials: boolean;
  /** Attach/replace/delete diplomas: Admin, Methodist, AccountManager. */
  canManageDiplomas: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_SEND_EMAIL` flag. */
  canSendDiplomaEmails: boolean;
  /** `canManageDiplomas` AND the off-by-default `DIPLOMA_GENERATION` flag. */
  canGenerateDiplomas: boolean;
  /**
   * ТЗ 0041 — видеть вкладку «Навыки» группы: Admin, AccountManager,
   * HRManager, Methodist (зеркало `CAREER_ROLES` на бэкенде).
   *
   * 🔴 Прятать вкладку обязательно, а не «желательно». Карточка группы
   * открыта шире карьерного домена, и роль без прав на `careerGroupSkills`
   * получит на клик не отказ, а пятисотку: маршрут и права на запрос — разные
   * оси, и на этом уже обжигались.
   */
  canSeeGroupSkills: boolean;
  /**
   * ТЗ 0075 — кнопка «Выгрузка для P&L» на вкладке «Финансы»: Admin,
   * FinanceManager (`FINANCE_PLAN_ROLES`, зеркало `PNL_EXPORT_ROLES` на
   * бэкенде). Уже, чем `canViewFinance`: аккаунт-менеджеру и продажам вкладка видна,
   * а заказ выгрузки сервер им не даст.
   */
  canExportPnl: boolean;
  /**
   * ERP-110 — «Исправить группу (ошибка ввода)» в меню строки ростера:
   * те же роли, что у перевода. Списки ролей — одни на приложение
   * (`agreementCorrectionModel`), общие с карточкой студента.
   */
  canCorrectAgreementGroup: boolean;
  /** ERP-110 — «Отменить зачисление»: Admin, FinanceManager, AccountManager. */
  canUndoEnrollment: boolean;
  /**
   * Кнопки шапки «Править», LMS, «В архив»: `EDUCATION_ROLES` (Admin,
   * AccountManager, SalesManager, HRManager) — те же роли, что у мутаций
   * группы на бэкенде и у «Править группу» в палитре.
   */
  canEditGroup: boolean;
  /**
   * «Добавить студента»: роли `appendStudentToGroup` на бэкенде — Admin,
   * AccountManager, SalesManager, FinanceManager (HR — нет).
   */
  canAppendStudent: boolean;
  /**
   * Меню синхронизации (Fakturownia / PandaDoc): роли их мутаций — Admin,
   * AccountManager, SalesManager, FinanceManager.
   */
  canSyncGroup: boolean;
  /**
   * Есть ли у роли список групп (`/education/groups`, маршрут `education`):
   * `EDUCATION_SECTION_ROLES`. Финансисту карточка группы открыта, а список —
   * нет, и «К списку групп» вело бы его на 404.
   */
  canOpenGroupList: boolean;
  /**
   * Учебные вкладки карточки — «Zoom», «Материалы», «ДЗ», «Опросы»: роли
   * «Обучения» (`EDUCATION_SECTION_ROLES`). Финансист открывает карточку
   * ради денег и дипломов, а запросы этих вкладок бэкенд ему не отдаёт — без
   * флага он получал бы на клик ошибку вместо вкладки.
   */
  canSeeZoomTab: boolean;
  canSeeMaterialsTab: boolean;
  canSeeHomeworkTab: boolean;
  canSeeSurveysTab: boolean;
}

/** Флаги видимости вкладок — из них `visibleGroupTabs` строит полосу. */
export type IGroupTabFlags = Pick<
  IGroupPermissions,
  | 'canSeeGroupSkills'
  | 'canSeeZoomTab'
  | 'canSeeMaterialsTab'
  | 'canSeeHomeworkTab'
  | 'canSeeSurveysTab'
>;

/**
 * Вкладки карточки группы, доступные роли, в каноническом порядке
 * (`GROUP_TAB_IDS`). Вкладка без флага видна всем, кому открыта карточка.
 */
export const visibleGroupTabs = (flags: IGroupTabFlags): TabId[] => {
  const gate: Partial<Record<TabId, boolean>> = {
    zoom: flags.canSeeZoomTab,
    materials: flags.canSeeMaterialsTab,
    skills: flags.canSeeGroupSkills,
    surveys: flags.canSeeSurveysTab,
    homework: flags.canSeeHomeworkTab,
  };
  return GROUP_TAB_IDS.filter((tabId) => gate[tabId] !== false);
};

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/**
 * Reads `userStateVar().me.roles` and maps `UserRole` to capability booleans,
 * mirroring the backend TZ permission notes.
 */
export const useGroupPermissions = (): IGroupPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canViewFinance = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.FINANCIER,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
  ]);

  const canTerminate = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.ACCOUNTANT,
    UserRole.SALESMANAGER,
    UserRole.FINANCIER,
  ]);

  const canRemindDebtors =
    DEBTOR_REMINDERS &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canIssueFaktura =
    INVOICE_ISSUE &&
    hasAnyRole(roles, [
      UserRole.ADMIN,
      UserRole.FINANCIER,
      UserRole.ACCOUNTANT,
    ]);

  const canManageMaterials =
    G6_MATERIALS_CRUD &&
    hasAnyRole(roles, [UserRole.ADMIN, UserRole.METHODIST, UserRole.TEACHER]);

  const canManageDiplomas = hasAnyRole(roles, [
    UserRole.ADMIN,
    UserRole.METHODIST,
    UserRole.ACCOUNTANT,
  ]);

  // Список берём ОДИН на приложение (`CAREER_ROLES`), а не переписываем
  // рядом: две копии одного правила расходятся молча, и разойтись они успеют
  // раньше, чем кто-нибудь это заметит.
  const canSeeGroupSkills = hasAnyRole(roles, CAREER_ROLES);

  // Один флаг на четыре учебные вкладки сегодня, но флаги — раздельные:
  // бэкенд открывает их запросы разными списками ролей.
  const canSeeLearningTabs = hasAnyRole(roles, EDUCATION_SECTION_ROLES);

  return {
    canViewFinance,
    canExportFinance: canViewFinance,
    canTerminate,
    canRemindDebtors,
    canIssueFaktura,
    canManageMaterials,
    canManageDiplomas,
    canSendDiplomaEmails: DIPLOMA_SEND_EMAIL && canManageDiplomas,
    canGenerateDiplomas: DIPLOMA_GENERATION && canManageDiplomas,
    canSeeGroupSkills,
    canExportPnl: hasAnyRole(roles, FINANCE_PLAN_ROLES),
    canCorrectAgreementGroup: hasAnyRole(roles, [...AGREEMENT_CORRECT_ROLES]),
    canUndoEnrollment: hasAnyRole(roles, [...ENROLLMENT_UNDO_ROLES]),
    canEditGroup: hasAnyRole(roles, EDUCATION_ROLES),
    // Тот же список, что у расторжения: Admin, AM, Sales, Finance.
    canAppendStudent: canTerminate,
    canSyncGroup: canTerminate,
    canOpenGroupList: hasAnyRole(roles, EDUCATION_SECTION_ROLES),
    canSeeZoomTab: canSeeLearningTabs,
    canSeeMaterialsTab: canSeeLearningTabs,
    canSeeHomeworkTab: canSeeLearningTabs,
    canSeeSurveysTab: canSeeLearningTabs,
  };
};
